import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {Link, useLocation, useNavigate} from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import supabase from "@/utils/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { useLoginModal } from "@/contexts/LoginModalContext";

interface LoginFormValues {
    email: string;
    password: string;
}

const LoginModal = () => {
    const { isLoginModalOpen, closeLoginModal } = useLoginModal();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Route değişince modal kapansın
        if (isLoginModalOpen) {
            closeLoginModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    const form = useForm<LoginFormValues>({
        defaultValues: {
            email: "",
            password: "",
        },
    });

    useEffect(() => {
        if (user && isLoginModalOpen) {
            closeLoginModal();
            // navigate('/');
        }
    }, [user, isLoginModalOpen, closeLoginModal, navigate]);

    const onSubmit = async (values: LoginFormValues) => {
        setIsLoading(true);
        if (!values.email || !values.password) {
            toast.error("Please check your email and password.");
            setIsLoading(false);
            return;
        }

        const { error } = await supabase.auth.signInWithPassword(values);
        if (error) {
            toast.error(error.message);
        } else {
            toast.success("Login successful!");
        }

        setIsLoading(false);
    };

    return (
        <Dialog open={isLoginModalOpen} onOpenChange={closeLoginModal}>
            <DialogContent className="max-w-md p-0 bg-background flex items-center justify-center">
                <div className="w-full bg-white dark:bg-gray-800 rounded-xl p-8">
                    <h2 className="text-2xl font-bold text-center mb-6 text-secondary dark:text-white">Welcome Back</h2>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="dark:text-gray-300">Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter your email"
                                                type="email"
                                                {...field}
                                                autoComplete="off"
                                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="dark:text-gray-300">Password</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter your password"
                                                type="password"
                                                {...field}
                                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button
                                className="w-full bg-[#E5F4EA] hover:bg-[#E5F4EA]/90 text-[#155231] dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90 dark:text-white"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Logging in..." : "Login"}
                            </Button>
                        </form>
                    </Form>
                    <div className="mt-4 text-center text-sm">
                        <Link
                            to="/forgot-password"
                            className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    <div className="mt-4 text-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Don't have an account?</span>{" "}
                        <Link
                            to="/signup"
                            className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white"
                        >
                            Sign up
                        </Link>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LoginModal;
